import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useSEO } from "../hooks/useSEO";

/* ── Helpers ─────────────────────────────────────────────────────────────── */
function formatDateFull(iso) {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  } catch { return iso; }
}

function formatMonthYear(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" }).toUpperCase();
  } catch { return iso; }
}

function groupByMonth(dates) {
  const groups = {};
  for (const d of dates) {
    const key = formatMonthYear(d.date);
    if (!groups[key]) groups[key] = [];
    groups[key].push(d);
  }
  return Object.entries(groups);
}

const SOCIAL_ICONS = {
  instagram: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
    </svg>
  ),
  tiktok: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.75a8.27 8.27 0 004.84 1.56V6.86a4.85 4.85 0 01-1.07-.17z"/>
    </svg>
  ),
  youtube: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M23.495 6.205a3.007 3.007 0 00-2.088-2.088c-1.87-.501-9.396-.501-9.396-.501s-7.507-.01-9.396.501A3.007 3.007 0 00.527 6.205a31.247 31.247 0 00-.522 5.805 31.247 31.247 0 00.522 5.783 3.007 3.007 0 002.088 2.088c1.868.502 9.396.502 9.396.502s7.506 0 9.396-.502a3.007 3.007 0 002.088-2.088 31.247 31.247 0 00.5-5.783 31.247 31.247 0 00-.5-5.805zM9.609 15.601V8.408l6.264 3.602z"/>
    </svg>
  ),
};

/* ── Film strip thumbnail ────────────────────────────────────────────────── */
function FilmThumb({ show, active, onClick }) {
  return (
    <button
      onClick={onClick}
      aria-label={`Sélectionner ${show.title}`}
      style={{
        flexShrink: 0,
        width: active ? 100 : 72,
        cursor: "pointer",
        background: "transparent",
        border: "none",
        padding: 0,
        transition: "width 0.5s cubic-bezier(0.22,1,0.36,1)",
        position: "relative",
      }}
    >
      <div style={{
        aspectRatio: "3 / 4",
        overflow: "hidden",
        border: `1px solid ${active ? "#C9A961" : "#1a1a1a"}`,
        transition: "border-color 0.4s ease",
        position: "relative",
      }}>
        {show.cover_image ? (
          <img
            src={show.cover_image}
            alt={show.title}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              filter: active ? "grayscale(20%)" : "grayscale(90%)",
              transition: "filter 0.6s ease",
            }}
          />
        ) : (
          <div style={{ width: "100%", height: "100%", background: "#0d0d0d" }} />
        )}
        {active && (
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: "#C9A961" }} />
        )}
      </div>
    </button>
  );
}

/* ── Page ────────────────────────────────────────────────────────────────── */
export default function Spectacles() {
  useSEO("Les Spectacles", "Tous les spectacles des artistes nominés au Stand d'Or 2026. Dates, billetterie et réseaux.");

  const [shows, setShows] = useState([]);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(true);
  const calendarRef = useRef(null);
  const stripRef = useRef(null);

  useEffect(() => {
    api.get("/shows")
      .then(({ data }) => { setShows(data || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const scrollToCalendar = () => {
    calendarRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const show = shows[active] || null;
  const allDates = shows.flatMap((s) =>
    (s.dates || []).map((d) => ({ ...d, show_title: s.title, show_artist: s.artist_name }))
  ).sort((a, b) => a.date.localeCompare(b.date));
  const upcomingDates = allDates.filter((d) => new Date(d.date) >= new Date());
  const calendarGroups = groupByMonth(upcomingDates);

  if (loading) return null;

  return (
    <div data-testid="spectacles-page" style={{ background: "#000" }}>

      {/* ── HERO SCÈNE ──────────────────────────────────────────────────── */}
      <section
        className="grain relative overflow-hidden"
        style={{ minHeight: "100vh", background: "#000", display: "flex", flexDirection: "column" }}
      >
        {/* Spotlight */}
        <div
          className="absolute pointer-events-none"
          style={{
            top: "-10%",
            left: "50%",
            transform: "translateX(-50%)",
            width: "60vw",
            height: "80vh",
            background: "radial-gradient(ellipse at 50% 0%, rgba(201,169,97,0.06) 0%, transparent 70%)",
          }}
          aria-hidden="true"
        />

        {/* Header */}
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 pt-[120px] w-full">
          <p className="label mb-6" style={{ color: "#C9A961" }}>Édition 2026 — Les spectacles</p>
          <h1
            className="font-serif"
            style={{ fontSize: "clamp(52px, 9vw, 140px)", lineHeight: 0.9, letterSpacing: "-0.03em", color: "#F8F6F0" }}
          >
            Sur scène.
          </h1>
        </div>

        {/* Film strip */}
        {shows.length > 0 && (
          <div
            className="relative max-w-[1400px] mx-auto px-6 lg:px-12 mt-12 w-full"
          >
            <div
              ref={stripRef}
              style={{
                display: "flex",
                gap: 12,
                overflowX: "auto",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                paddingBottom: 8,
              }}
            >
              {shows.map((s, i) => (
                <FilmThumb key={s.id} show={s} active={i === active} onClick={() => setActive(i)} />
              ))}
            </div>
            <div className="hairline mt-3" />
          </div>
        )}

        {/* Show detail */}
        {show ? (
          <div className="relative flex-1 max-w-[1400px] mx-auto px-6 lg:px-12 py-16 w-full">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">

              {/* Cover large */}
              <div className="md:col-span-4 lg:col-span-3">
                <div style={{ aspectRatio: "3 / 4", overflow: "hidden", position: "relative", background: "#0d0d0d" }}>
                  {show.cover_image ? (
                    <img
                      src={show.cover_image}
                      alt={show.title}
                      style={{ width: "100%", height: "100%", objectFit: "cover", filter: "grayscale(15%)" }}
                    />
                  ) : (
                    <div style={{ width: "100%", height: "100%", background: "#0d0d0d", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <p className="label" style={{ color: "#2a2a2a", fontSize: 10 }}>COVER À VENIR</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Infos */}
              <div className="md:col-span-7 md:col-start-6 lg:col-span-8 lg:col-start-5">
                <p className="label mb-4" style={{ color: "rgba(201,169,97,0.6)", fontSize: 10 }}>
                  {show.artist_name.toUpperCase()}
                </p>
                <h2
                  className="font-serif"
                  style={{ fontSize: "clamp(40px, 6vw, 96px)", lineHeight: 0.92, color: "#F8F6F0", letterSpacing: "-0.025em", marginBottom: 24 }}
                >
                  {show.title}
                </h2>

                {show.description && (
                  <p style={{ color: "rgba(248,246,240,0.65)", fontSize: 16, lineHeight: 1.75, maxWidth: 560, marginBottom: 32 }}>
                    {show.description}
                  </p>
                )}

                {/* Réseaux */}
                {(show.instagram || show.tiktok || show.youtube) && (
                  <div style={{ display: "flex", gap: 16, marginBottom: 32 }}>
                    {["instagram", "tiktok", "youtube"].map((net) =>
                      show[net] ? (
                        <a
                          key={net}
                          href={show[net]}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "rgba(245,241,232,0.35)", transition: "color 0.3s ease" }}
                          onMouseOver={(e) => (e.currentTarget.style.color = "#C9A961")}
                          onMouseOut={(e) => (e.currentTarget.style.color = "rgba(245,241,232,0.35)")}
                          aria-label={net}
                        >
                          {SOCIAL_ICONS[net]}
                        </a>
                      ) : null
                    )}
                  </div>
                )}

                {/* Prochaines dates du show */}
                {(show.dates || []).filter((d) => new Date(d.date) >= new Date()).slice(0, 3).length > 0 && (
                  <div style={{ marginBottom: 32 }}>
                    <p className="label mb-4" style={{ fontSize: 9, color: "rgba(201,169,97,0.5)" }}>PROCHAINES DATES</p>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {show.dates.filter((d) => new Date(d.date) >= new Date()).slice(0, 3).map((d) => (
                        <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 16, borderBottom: "1px solid #1a1a1a", paddingBottom: 8 }}>
                          <p className="label" style={{ fontSize: 10, color: "#C9A961", minWidth: 100 }}>
                            {new Date(d.date).toLocaleDateString("fr-FR", { day: "numeric", month: "short" }).toUpperCase()}
                          </p>
                          <p style={{ color: "rgba(248,246,240,0.7)", fontSize: 14, flex: 1 }}>
                            {d.venue} — {d.city}
                          </p>
                          {d.status === "sold_out" ? (
                            <span className="label" style={{ fontSize: 9, color: "rgba(248,246,240,0.25)" }}>COMPLET</span>
                          ) : d.ticket_url ? (
                            <a
                              href={d.ticket_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="label"
                              style={{ fontSize: 9, color: "#C9A961", borderBottom: "1px solid rgba(201,169,97,0.35)", paddingBottom: 1, textDecoration: "none" }}
                            >
                              Billets →
                            </a>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {show.ticketing_url && (
                  <a
                    href={show.ticketing_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-ghost"
                    style={{ display: "inline-flex", textDecoration: "none" }}
                  >
                    Billetterie officielle →
                  </a>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="relative flex-1 max-w-[1400px] mx-auto px-6 lg:px-12 py-32 w-full">
            <p className="font-serif italic" style={{ fontSize: "clamp(20px, 2.5vw, 32px)", color: "rgba(248,246,240,0.2)", maxWidth: 480 }}>
              Les spectacles seront annoncés prochainement.
            </p>
          </div>
        )}

        {/* Scroll indicator → Calendrier */}
        {upcomingDates.length > 0 && (
          <div
            style={{ display: "flex", flexDirection: "column", alignItems: "center", paddingBottom: 40, cursor: "pointer", gap: 8 }}
            onClick={scrollToCalendar}
            role="button"
            aria-label="Voir le calendrier"
          >
            <p className="label" style={{ fontSize: 9, letterSpacing: "0.25em", color: "rgba(245,241,232,0.2)" }}>
              CALENDRIER
            </p>
            <div style={{ position: "relative", height: 56, display: "flex", flexDirection: "column", alignItems: "center" }}>
              <div style={{ width: 1, height: "100%", background: "rgba(201,169,97,0.2)" }} />
              <div
                className="mirror-scroll-dot"
                style={{ position: "absolute", width: 4, height: 4, borderRadius: "50%", background: "#C9A961", left: "50%", transform: "translateX(-50%)" }}
              />
            </div>
          </div>
        )}
      </section>

      {/* ── CALENDRIER ──────────────────────────────────────────────────── */}
      {upcomingDates.length > 0 && (
        <section
          ref={calendarRef}
          style={{ background: "#000", borderTop: "1px solid #1c1c1c" }}
          data-testid="spectacles-calendar"
        >
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 md:py-36">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-20">
              <div className="md:col-span-5">
                <p className="label mb-6" style={{ color: "#C9A961" }}>Toutes les dates</p>
                <h2 className="font-serif" style={{ fontSize: "clamp(40px, 5vw, 80px)", lineHeight: 1, color: "#F8F6F0" }}>
                  Calendrier<br />
                  <em style={{ color: "rgba(248,246,240,0.3)" }}>2026</em>
                </h2>
              </div>
              <div className="md:col-span-5 md:col-start-8 flex items-end">
                <p style={{ color: "rgba(248,246,240,0.45)", fontSize: 15, lineHeight: 1.7 }}>
                  Toutes les représentations des artistes nominés au Stand d'Or, réunies en un seul calendrier.
                </p>
              </div>
            </div>

            {/* Par mois */}
            <div style={{ borderTop: "1px solid #1c1c1c" }}>
              {calendarGroups.map(([month, dates]) => (
                <div key={month} style={{ borderBottom: "1px solid #0f0f0f", paddingTop: 32, paddingBottom: 8 }}>
                  <p className="label mb-6" style={{ fontSize: 10, color: "rgba(201,169,97,0.5)", letterSpacing: "0.2em" }}>
                    {month}
                  </p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                    {dates.map((d) => (
                      <div
                        key={d.id}
                        className="grid grid-cols-12 gap-4 py-5 group"
                        style={{ borderTop: "1px solid #0f0f0f", alignItems: "center" }}
                      >
                        {/* Jour */}
                        <div className="col-span-2 md:col-span-1">
                          <p className="font-serif" style={{ fontSize: "clamp(28px, 3vw, 44px)", color: "#F8F6F0", lineHeight: 0.9, letterSpacing: "-0.02em" }}>
                            {new Date(d.date).getDate()}
                          </p>
                          <p className="label" style={{ fontSize: 9, color: "rgba(245,241,232,0.25)", marginTop: 2 }}>
                            {new Date(d.date).toLocaleDateString("fr-FR", { weekday: "short" }).toUpperCase()}
                          </p>
                        </div>

                        {/* Show + Salle */}
                        <div className="col-span-6 md:col-span-6">
                          <p className="label mb-1" style={{ fontSize: 9, color: "rgba(201,169,97,0.5)" }}>
                            {d.show_artist?.toUpperCase()} — {d.show_title}
                          </p>
                          <p className="font-serif" style={{ fontSize: "clamp(17px, 1.8vw, 24px)", color: "#F8F6F0", lineHeight: 1.15 }}>
                            {d.venue}
                          </p>
                          {d.notes && (
                            <p style={{ color: "rgba(248,246,240,0.3)", fontSize: 13, marginTop: 4 }}>{d.notes}</p>
                          )}
                        </div>

                        {/* Ville */}
                        <div className="col-span-2 md:col-span-2">
                          <p className="label" style={{ fontSize: 10, color: "rgba(248,246,240,0.4)" }}>
                            {d.city}
                            {d.country !== "France" && <span style={{ color: "rgba(248,246,240,0.2)" }}> — {d.country}</span>}
                          </p>
                        </div>

                        {/* Statut / Billet */}
                        <div className="col-span-2 md:col-span-3 flex justify-end">
                          {d.status === "sold_out" ? (
                            <span className="label" style={{ fontSize: 9, color: "rgba(248,246,240,0.2)", letterSpacing: "0.15em" }}>COMPLET</span>
                          ) : d.status === "cancelled" ? (
                            <span className="label" style={{ fontSize: 9, color: "rgba(248,246,240,0.2)", letterSpacing: "0.15em", textDecoration: "line-through" }}>ANNULÉ</span>
                          ) : d.ticket_url ? (
                            <a
                              href={d.ticket_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="label"
                              style={{ fontSize: 10, color: "#C9A961", borderBottom: "1px solid rgba(201,169,97,0.35)", paddingBottom: 2, textDecoration: "none", transition: "border-color 0.3s" }}
                              onMouseOver={(e) => (e.currentTarget.style.borderColor = "#C9A961")}
                              onMouseOut={(e) => (e.currentTarget.style.borderColor = "rgba(201,169,97,0.35)")}
                            >
                              Réserver →
                            </a>
                          ) : (
                            <span className="label" style={{ fontSize: 9, color: "rgba(248,246,240,0.15)" }}>BILLETTERIE À VENIR</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── EMPTY STATE calendrier ───────────────────────────────────────── */}
      {upcomingDates.length === 0 && shows.length > 0 && (
        <section style={{ background: "#000", borderTop: "1px solid #1c1c1c" }} ref={calendarRef}>
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24">
            <p className="font-serif italic" style={{ fontSize: "clamp(20px, 2.5vw, 32px)", color: "rgba(248,246,240,0.15)", maxWidth: 480 }}>
              Les dates de représentation seront annoncées prochainement.
            </p>
          </div>
        </section>
      )}
    </div>
  );
}
